import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FilterIcon from '@/component-library/primitives/Icons/FilterIcon/FilterIcon';
import InfoIcon from '@/component-library/primitives/Icons/InfoIcon/InfoIcon';
import SearchMagnifyingGlassIcon from '@/component-library/primitives/Icons/SearchMagnifyingGlassIcon/SearchMagnifyingGlassIcon';
import Tooltip from '@/component-library/primitives/Tooltip/Tooltip';

interface SearchFieldProps {
  initialValue?: string | number;
  setSearchValue?: (value: any) => void;
  debounce?: number;
  className?: string;
  withFilter?: boolean;
  onFilterClick?: () => void;
  filterTooltipContent?: React.ReactNode;
  hasActiveFilters?: boolean;
}

const SearchField: React.FC<SearchFieldProps> = ({
  setSearchValue,
  debounce = 500,
  initialValue,
  className,
  withFilter = true,
  onFilterClick,
  filterTooltipContent,
  hasActiveFilters = false,
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState(initialValue);

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearchValue && setSearchValue(value);
    }, debounce);

    return () => clearTimeout(timeout);
  }, [value]);

  return (
    <div className="bg-custom-silver w-fit rounded-lg flex flex-row items-center relative overflow-hidden">
      <SearchMagnifyingGlassIcon width={16} className="mx-3" />
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder={t('SearchField.placeholder')}
        className={`text-custom-black placeholder-custom-darkSilver text-sm bg-custom-silver text-sm w-[18rem] p-2 outline-none ${className}`}
      />

      {filterTooltipContent && (
        <Tooltip
          content={filterTooltipContent}
          side="bottom"
          contentClassName="bg-gray-200 text-gray-800 rounded-lg shadow-md p-3 max-w-xs z-50"
        >
          <div className="px-2 py-2 cursor-pointer" aria-label="Search information">
            <InfoIcon width={16} height={16} color="#9CA3AF" />
          </div>
        </Tooltip>
      )}

      {withFilter && (
        <button
          type="button"
          onClick={onFilterClick}
          className="group border-l border-l-custom-mediumSilver text-custom-darkSilver text-sm px-3 py-2 bg-custom-silver hover:text-custom-red relative"
          aria-label="Open filters"
        >
          <span className="[&>*_path]:stroke-custom-darkSilver [&>*_path]:transition group-hover:[&>*_path]:stroke-custom-black">
            <FilterIcon width={16} />
          </span>
          {hasActiveFilters && <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-custom-red rounded-full"></span>}
        </button>
      )}
    </div>
  );
};

export default SearchField;
